import { Helmet } from "react-helmet-async";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export type ServiceFAQItem = { q: string; a: string };

type Props = {
  title?: string;
  subtitle?: string;
  items: ServiceFAQItem[];
  /** Stable id used to dedupe the injected JSON-LD across remounts. */
  id: string;
};

/**
 * Per-page FAQ accordion with FAQPage JSON-LD schema.
 * JSON-LD is emitted at render time via Helmet so it appears in the SSG'd
 * HTML (visible to non-JS crawlers).
 */
export default function ServiceFAQ({ title, subtitle, items, id }: Props) {
  if (!items?.length) return null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };


  return (
    <section className="py-16 md:py-20 bg-secondary/40">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <div className="container max-w-3xl">
        <div className="text-center reveal">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-primary text-balance">
            {title ?? "Häufige Fragen"}
          </h2>
          {subtitle && (
            <p className="mt-4 text-lg text-muted-foreground leading-relaxed">{subtitle}</p>
          )}
        </div>

        <div className="mt-10 reveal">
          <Accordion type="single" collapsible className="space-y-3">
            {items.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${id}-${i}`}
                className="rounded-xl border border-border bg-card px-5 shadow-card data-[state=open]:shadow-elegant transition-all"
              >
                <AccordionTrigger className="text-left font-display text-lg font-semibold text-primary hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
